/**
 * Transport for the RAG REST surface
 * (`/api/debate/rag/upload | list | :doc_id`).
 *
 * Server-side we bind documents to a `session_id` (mandatory) and an
 * optional `persona_id`. When `personaId` is omitted, the upload lands in
 * the council pool (visible to every speaker).
 */
import { getCsrfToken, withCsrfHeader } from "./csrf";

export interface RagDocument {
  id: string;
  session_id: string;
  persona_id: string | null;
  filename: string;
  mime: string;
  byte_size: number;
  page_count: number | null;
  chunk_count: number;
  status: "pending" | "ready" | "failed";
  error: string | null;
  ingested_at: string | null;
  created_at: string;
}

const BASE = "/api/debate/rag";

async function _req<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    credentials: "include",
    ...init,
    headers: withCsrfHeader(init?.headers as Record<string, string> | undefined),
  });
  if (!r.ok) {
    let detail = "";
    try {
      const j = await r.json();
      detail = j?.detail ? `: ${j.detail}` : "";
    } catch {
      /* noop */
    }
    throw new Error(`RAG HTTP ${r.status}${detail}`);
  }
  return (await r.json()) as T;
}

export interface UploadOpts {
  sessionId: string;
  personaId?: string | null;
  file: File;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
  /**
   * Hard ceiling for the whole upload (browser → nginx). Defaults to
   * 4 minutes which comfortably covers a 20 MiB doc on a 1 Mbit link
   * plus the 5-minute backend ingest budget set in the nginx vhost.
   * Without this `xhr` would hang forever if the network half-closes.
   */
  timeoutMs?: number;
  /**
   * Number of automatic retries when the upload aborts due to a
   * transient network failure (`onerror` / progress freeze). The same
   * `File` object is re-streamed; the backend deduplicates by content
   * hash so a second attempt is safe.
   */
  retries?: number;
}

const STALL_GRACE_MS = 30_000;

function _uploadOnce(
  opts: UploadOpts,
  attempt: number,
): Promise<RagDocument> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fd = new FormData();
    fd.append("file", opts.file, opts.file.name);
    fd.append("session_id", opts.sessionId);
    if (opts.personaId) fd.append("persona_id", opts.personaId);

    xhr.open("POST", `${BASE}/upload`, true);
    xhr.withCredentials = true;
    xhr.timeout = opts.timeoutMs ?? 240_000;
    const csrfToken = getCsrfToken();
    if (csrfToken) xhr.setRequestHeader("X-Macdm-Csrf", csrfToken);

    let lastTick = Date.now();
    let lastLoaded = 0;
    let stallTimer: ReturnType<typeof setInterval> | null = null;
    const cleanup = () => {
      if (stallTimer) {
        clearInterval(stallTimer);
        stallTimer = null;
      }
    };

    // Bu dinleyici KOŞULSUZ bağlanır. Daha önce yalnız `opts.onProgress`
    // verildiğinde bağlanıyordu; çağıran ilerleme istemediğinde `lastTick`
    // hiç güncellenmiyor ve aşağıdaki sayaç 30 sn sonra SAĞLIKLI bir
    // yüklemeyi iptal ediyordu.
    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      if (ev.loaded !== lastLoaded) {
        lastLoaded = ev.loaded;
        lastTick = Date.now();
      }
      opts.onProgress?.(ev.loaded, ev.total);
    };

    // Gövde tamamen gittiğinde ilerleme olayı ÜRETİLMEZ; bundan sonrası
    // sunucunun işleme süresidir (taranmış PDF'lerde OCR 150 sn'ye kadar
    // sürebilir). Sayaç bu aşamada da çalışırsa her OCR yüklemesi 30.
    // saniyede "takıldı" diye iptal edilir — takip sayacı yalnız AKTARIM
    // aşamasına aittir, ondan sonrasını `xhr.timeout` korur.
    xhr.upload.onloadend = () => cleanup();

    // Browsers do NOT always fire `onerror` when an upload silently
    // freezes (e.g. nginx back-pressure stalls XHR forever). We poll
    // for "no progress in N seconds" and force an abort so the caller
    // sees a real failure (and can retry) instead of hanging UI.
    stallTimer = setInterval(() => {
      if (Date.now() - lastTick > STALL_GRACE_MS) {
        cleanup();
        try {
          xhr.abort();
        } catch {
          /* noop */
        }
        reject(new Error(`RAG upload stalled (>${STALL_GRACE_MS / 1000}s no progress)`));
      }
    }, 5_000);

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as RagDocument);
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      } else {
        let detail = "";
        try {
          const raw: unknown = JSON.parse(xhr.responseText)?.detail;
          if (typeof raw === "string") detail = raw.trim();
        } catch {
          /* noop */
        }
        // 413 nginx'ten gelir, uygulamadan değil: gövde bir HTML hata
        // sayfasıdır, `detail` yoktur. Kullanıcıya çıplak kod göstermek
        // yerine sınırı söyle — yapabileceği tek şey dosyayı küçültmek.
        if (!detail && xhr.status === 413) {
          detail = "Dosya çok büyük — en fazla 25 MB yükleyebilirsiniz.";
        }
        // Arka uç artık kullanıcıya dönük Türkçe cümleler döndürüyor.
        // "RAG upload HTTP 422: " önekini başa koymak dar yükleme
        // kartında asıl cümleyi kırpıyordu; kod yalnız açıklama
        // yoksa gösterilir.
        reject(new Error(detail || `Yükleme başarısız (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("RAG upload network error"));
    };
    xhr.upload.onerror = () => {
      cleanup();
      reject(new Error("RAG upload transport error"));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new Error(`RAG upload timeout (>${(xhr.timeout / 1000) | 0}s)`));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new Error("RAG upload aborted"));
    };
    if (opts.signal) {
      if (opts.signal.aborted) {
        cleanup();
        reject(new Error("RAG upload aborted"));
        return;
      }
      opts.signal.addEventListener(
        "abort",
        () => {
          cleanup();
          try {
            xhr.abort();
          } catch {
            /* noop */
          }
        },
        { once: true },
      );
    }
    void attempt;
    xhr.send(fd);
  });
}

export async function uploadDocument(opts: UploadOpts): Promise<RagDocument> {
  const maxAttempts = Math.max(1, (opts.retries ?? 1) + 1);
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await _uploadOnce(opts, attempt);
    } catch (e) {
      lastError = e;
      // User-cancelled → never retry.
      if (opts.signal?.aborted) throw e;
      const msg = e instanceof Error ? e.message : String(e);
      // Retry only on transient transport/stall classes; HTTP errors
      // (413/422/502) are deterministic and would just repeat.
      const transient =
        msg.includes("network error") ||
        msg.includes("transport error") ||
        msg.includes("stalled") ||
        msg.includes("timeout");
      if (!transient || attempt >= maxAttempts) throw e;
      // Reset progress callback so the dropzone bar restarts cleanly.
      opts.onProgress?.(0, opts.file.size);
      await new Promise((r) => setTimeout(r, 750 * attempt));
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("RAG upload failed");
}

export async function listDocuments(args: {
  sessionId: string;
  personaId?: string | "pool" | null;
  signal?: AbortSignal;
}): Promise<RagDocument[]> {
  const params = new URLSearchParams();
  params.set("session_id", args.sessionId);
  if (args.personaId !== undefined && args.personaId !== null) {
    params.set("persona_id", String(args.personaId));
  }
  const init: RequestInit = { method: "GET" };
  if (args.signal) init.signal = args.signal;
  const data = await _req<{ documents: RagDocument[] }>(
    `${BASE}/list?${params.toString()}`,
    init,
  );
  return data.documents;
}

export async function deleteDocument(docId: string): Promise<void> {
  await _req(`${BASE}/${encodeURIComponent(docId)}`, { method: "DELETE" });
}
